/**
 * Which carriers each market names on the product page.
 *
 * Taken from what that market's own delivery page publishes
 * (`content/<locale>/delivery.md`), so the accordion cannot promise a carrier
 * the policy page doesn't. Checkout's shipping methods stay the authority on
 * what a given basket can actually be sent with; this is the shorthand under
 * "Delivery and returns".
 */

export interface DeliveryBadge {
  /** `carrier:*`, from `layers/ui/app/assets/logos/carriers`. */
  icon?: string
  /**
   * A raster logo under `public/`, for a carrier with no SVG. The icon
   * collection is an iconify one and takes SVG only, so these can't live
   * alongside the rest — `intrinsic` is the file's own pixel size, which is
   * what the rendered box is scaled from.
   */
  image?: string
  intrinsic?: { width: number, height: number }
  /** Carrier name, which reads the same in every language. */
  label: string
  /**
   * Height in px. These are bare wordmarks with no plate around them and wildly
   * different proportions — PPL is over 7:1 where DHL is 1.6:1 — so a single
   * height would leave the wide ones running two and a half times the width of
   * the narrow ones. Each is set to land in roughly the same width band
   * instead, which is what makes the row read as evenly weighted.
   */
  height?: number
}

/** What a squarish wordmark gets; the long ones override it. */
export const DELIVERY_BADGE_HEIGHT = 16

const DHL: DeliveryBadge = { icon: 'carrier:dhl', label: 'DHL' }
const DPD: DeliveryBadge = { icon: 'carrier:dpd', label: 'DPD' }
const INPOST: DeliveryBadge = { icon: 'carrier:inpost', label: 'InPost' }
const PACKETA: DeliveryBadge = { icon: 'carrier:packeta', label: 'Packeta' }
const ZASILKOVNA: DeliveryBadge = { icon: 'carrier:zasilkovna', label: 'Zásilkovna' }

// ~4.7:1 and ~7.1:1. Left at the default they would run 76px and 113px wide
// against DHL's 25px.
const POCZTA_POLSKA: DeliveryBadge = { icon: 'carrier:poczta-polska', label: 'Poczta Polska', height: 12 }
const PPL: DeliveryBadge = { icon: 'carrier:ppl', label: 'PPL', height: 9 }

// ~6.4:1, so it sits with PPL at the wide end of the band rather than at the
// default height. The only raster in here: Hermes has no SVG, and the supplied
// file is a transparent WebP, which is why it needs no plate behind it.
const HERMES: DeliveryBadge = {
  image: '/images/carriers/hermes.webp',
  intrinsic: { width: 300, height: 47 },
  label: 'Hermes',
  height: 10,
}

/** Keyed by the locale's country (see `StrictLocaleObject.country`). */
export const DELIVERY_BADGES_BY_COUNTRY: Record<string, DeliveryBadge[]> = {
  CZ: [PPL, ZASILKOVNA, DHL],
  SK: [PPL, PACKETA, DHL],
  PL: [INPOST, DHL, POCZTA_POLSKA, PACKETA],
  DE: [DHL, HERMES, PACKETA],

  // The two English storefronts, under the stand-in country their locale
  // carries rather than a market of their own: US is `en`, IE is `eu` (see
  // `shared/locales`). Neither ships on one country's network, so each row is
  // what their shared delivery page names — `content_eu` reads `content/en/` —
  // narrowed to the carriers that locale's channel actually runs. `en` reaches
  // DPD in Austria and Luxembourg; `en-eu-channel` carries no DPD method at
  // all, so `eu` does not claim one.
  //
  // FedEx flies the long-haul legs of both (GB, CH, NO, US, CA and the
  // Balkans) and is absent here only because the carrier collection has no
  // FedEx logo to show.
  US: [DHL, PPL, DPD],
  IE: [DHL, PPL],
}

/**
 * The logo for the courier an order was sent with — the courier part of its
 * shipping method code (`ZASILKOVNA_IN_POST`, `PPL_SMART_CZ`), which is also
 * what a saved pickup point records as `courier` — in the country the parcel
 * goes to.
 *
 * The logo is the one on the door the customer walks up to, so partner networks
 * resolve to the partner, but only where the code actually means the partner:
 * - `ZASILKOVNA_IN_POST` / `ZASILKOVNA_POCZTA_POLSKA` are InPost lockers and post
 *   offices in Poland only. In CZ/SK `ZASILKOVNA_IN_POST` is reused as the plain
 *   Zásilkovna pickup courier (see `getCourierNameFilter` in utils/shipping).
 * - `PPL_SMART_EUROPE` delivers to DHL Packstations in Germany — the reason it
 *   asks for a DHL Postnummer there.
 * Zásilkovna trades as Packeta outside the Czech Republic, as the badge rows
 * above do. Undefined for a courier with no logo, so the caller shows the
 * method name alone.
 */
export function deliveryBadgeForCourier(courier?: string | null, country?: string | null): DeliveryBadge | undefined {
  const code = courier?.trim().toUpperCase()
  if (!code)
    return undefined
  const cc = country?.trim().toUpperCase()
  if (code === 'PPL_SMART_EUROPE' && cc === 'DE')
    return DHL
  if (code.startsWith('PPL'))
    return PPL
  if (code.startsWith('DHL'))
    return DHL
  if (code === 'ZASILKOVNA_IN_POST' && cc === 'PL')
    return INPOST
  if (code === 'ZASILKOVNA_POCZTA_POLSKA' && cc === 'PL')
    return POCZTA_POLSKA
  if (code.startsWith('ZASILKOVNA'))
    return cc === 'CZ' ? ZASILKOVNA : PACKETA
  return undefined
}
